/**
 * WishlistTab - Wishlist management tab component
 * Redesigned with new hero card layout, tax calculations, and grid-style items
 */
import { useState, useEffect, useRef } from "react";
import { createPortal } from "react-dom";
import { toast } from "sonner";
import customToast from "../../utils/customToast";
import { Plus, Trash2, ExternalLink, ShoppingBag, Link as LinkIcon, Check, X, HelpCircle, Loader2, Lock, Percent, Edit3, Camera } from "lucide-react";
import { Button } from "../ui/button";
import { Input } from "../ui/input";
import { formatCurrency, formatCurrencyWhole, getCurrentCurrency } from "../../utils/formatters";
import { useSubscription } from "../../contexts/SubscriptionContext";
import { useTour } from "../../contexts/TourContext";
import QuizBox from "../common/QuizBox";
import InvestmentCostCalculator from "../common/InvestmentCostCalculator";
import ThinkItOverSheet, { VERDICT_STYLE } from "../wishlist/ThinkItOverSheet";
import { usePlan } from "../../utils/plan"; // plan_10

// Offered in the emoji picker. Anything else can be typed in.
const WISHLIST_EMOJIS = ['📲', '📱', '💻', '⌚', '🎧', '📷', '🎮', '👟',
  '👗', '👜', '💄', '🧴', '🛋️', '🪴', '🍳', '☕',
  '🚲', '📚', '💍', '🕶️', '🧸'];

const WishlistTab = ({ wishlistItems = [], saveUserData, userData, theme = 'pink' }) => {
  const { hasAccess, showFeaturePaywall, purchaseSubscription, purchasing, subscription, loading } = useSubscription();
  const tour = useTour();
  // Bypass the premium gate for the ENTIRE duration of the tour (welcome,
  // in_progress, and celebration phases). This is wider than just the
  // wishlist step — without it, the wishlist tab flashes "locked" briefly
  // when the celebration modal appears at the end of the tour.
  const isPremium = loading || hasAccess('wishlist') || !!tour?.isTourActive;

  const [newItem, setNewItem] = useState('');
  const [newValue, setNewValue] = useState('');
  // While typing in the add card, room is made below it and the page scrolls
  // so the whole card, price box included, sits above the keyboard.
  const addCardRef = useRef(null);
  const addEyebrowRef = useRef(null);
  const [addTyping, setAddTyping] = useState(false);
  // How far the page is slid up while typing in the add card. The app's
  // page can't be scrolled from code and the keyboard sits over it, so the
  // page slides up instead, putting the card just under the top of the screen.
  const [kbShift, setKbShift] = useState(0);
  const kbShiftRef = useRef(0);
  // While the page is slid up, a drag closes the keyboard instead of trying
  // to scroll a page that is being held in place (which is what shook).
  useEffect(() => {
    if (!kbShift) return undefined;
    let startY = null;
    const onStart = (e) => { startY = e.touches && e.touches[0] ? e.touches[0].clientY : null; };
    const onMove = (e) => {
      if (startY === null || !e.touches || !e.touches[0]) return;
      if (e.cancelable) e.preventDefault();
      if (Math.abs(e.touches[0].clientY - startY) > 8) {
        startY = null;
        const el = document.activeElement;
        if (el && typeof el.blur === 'function') el.blur();
      }
    };
    document.addEventListener('touchstart', onStart, { passive: true });
    document.addEventListener('touchmove', onMove, { passive: false });
    return () => {
      document.removeEventListener('touchstart', onStart);
      document.removeEventListener('touchmove', onMove);
    };
  }, [kbShift]);
  const onAddFocus = () => {
    setAddTyping(true);
    const t = addEyebrowRef.current;
    if (!t) return;
    // Where the eyebrow would be with no slide applied.
    const natural = t.getBoundingClientRect().top + kbShiftRef.current;
    const next = Math.max(0, Math.round(natural - 56));
    kbShiftRef.current = next;
    setKbShift(next);
  };
  const onAddBlur = () => {
    // Moving between boxes in the card blurs one before focusing the next,
    // so wait a moment and only drop the room if focus has left the card.
    setTimeout(() => {
      const el = addCardRef.current;
      if (!el || !el.contains(document.activeElement)) {
        setAddTyping(false);
        kbShiftRef.current = 0;
        setKbShift(0);
      }
    }, 150);
  };
  const [newUrl, setNewUrl] = useState('');
  const [newImageUrl, setNewImageUrl] = useState('');
  // An emoji stands in for a photo. Picking one clears the other.
  const [newEmoji, setNewEmoji] = useState('');
  const [emojiPickerOpen, setEmojiPickerOpen] = useState(false);
  const [customEmoji, setCustomEmoji] = useState('');
  const [emojiTyping, setEmojiTyping] = useState(false);
  // The item whose Think it over sheet is open.
  const [thinkItem, setThinkItem] = useState(null);

  // Freeze the page behind the emoji picker (same lock as Think it over).
  useEffect(() => {
    if (!emojiPickerOpen) return undefined;
    const y = window.scrollY;
    const b = document.body.style;
    const prev = { position: b.position, top: b.top, left: b.left, right: b.right, width: b.width, overflow: b.overflow };
    b.position = 'fixed'; b.top = '-' + y + 'px'; b.left = '0'; b.right = '0'; b.width = '100%'; b.overflow = 'hidden';
    return () => {
      Object.assign(b, prev);
      window.scrollTo(0, y);
    };
  }, [emojiPickerOpen]);
  const [isUploading, setIsUploading] = useState(false);
  const [isLoading, setIsLoading] = useState(false);
  const [selectedItems, setSelectedItems] = useState([]);
  const [editingTaxRate, setEditingTaxRate] = useState(false);
  const [showWishlistInfo, setShowWishlistInfo] = useState(false);
  const [taxRateInput, setTaxRateInput] = useState('');

  // Theme-aware colors
  const isWarm = theme === 'warm';
  const isPink = theme === 'pink';
  const plan = usePlan(); // plan_10

  // Theme-aware tile palette — sourced from Budget's Income/Spent/Saved-Inv/Leftover tiles
  // (BudgetHero.jsx) so Wishlist's tiles stay in the same family per theme.
  // Wishlist uses Leftover (lavender) + Saved-Inv (green) — green is intentionally shared
  // with Calendar's Saved-Inv (green) + Income (pink) pairing (Layemi's call).
  const tileColors = {
    green: isPink
      ? { bg: 'linear-gradient(135deg, #E7F1DB 0%, #D3E5BE 100%)', text: '#5C7A4A' }
      : isWarm
      ? { bg: 'linear-gradient(135deg, #E9EFD9 0%, #DCE6C1 100%)', text: '#5C7A4A' }
      : { bg: 'linear-gradient(135deg, #DCEFF0 0%, #C9E4E6 100%)', text: '#2F7A80' },
    lavender: isPink
      ? { bg: 'linear-gradient(135deg, #E9E1F5 0%, #D5C7EB 100%)', text: '#7A5FB0' }
      : isWarm
      ? { bg: 'linear-gradient(135deg, #F0E3D9 0%, #E5D0C2 100%)', text: '#9A5B3C' }
      : { bg: 'linear-gradient(135deg, #E4E1F5 0%, #D2CDEB 100%)', text: '#6B5FB0' },
  };

  const API_URL = process.env.REACT_APP_BACKEND_URL;

  // Get tax rate from user data or default to 13%
  const taxRate = userData?.wishlist_tax_rate ?? 13;

  const accentRGB = isPink ? '233,30,140' : isWarm ? '124,92,58' : '99,102,241';
  const heroStyle = {
    background: `linear-gradient(140deg, rgba(${accentRGB},0.06) 0%, rgba(${accentRGB},0.0) 60%), #ffffff`,
    boxShadow: `0 1px 3px rgba(${accentRGB},0.06), 0 4px 16px rgba(${accentRGB},0.08)`,
    border: `1px solid rgba(${accentRGB},0.08)`,
  };

  // If not premium, show locked state - centered on page
  if (!isPremium) {
    return (
      <div className="flex items-center justify-center min-h-[calc(100vh-180px)] p-4" data-testid="wishlist-paywall-container">
        <div className="bg-white rounded-2xl shadow-xl p-6 max-w-xs text-center">
          <div className="mb-3" style={{ fontSize: '44px', lineHeight: 1 }}>{'\uD83D\uDD12'}</div>
          <h3 className="mb-2" style={{ fontFamily: "'DM Serif Display', serif", fontSize: '24px', lineHeight: 1.1, color: '#1a1a1a', letterSpacing: '-0.01em' }}>Wishlist is Premium</h3>
          <p className="text-[15px] text-gray-500 mb-4">
            Track the purchases you want to make, then come back later to decide if they're worth it.
          </p>
          {!subscription?.trial_start ? (
            <Button
              onClick={() => showFeaturePaywall('wishlist')}
              className="w-full py-3.5 rounded-2xl text-[15px] font-bold border-0 shadow-none"
              style={{ background: isWarm ? '#F0E6D8' : isPink ? '#EDE6F5' : '#EEF1FE', color: isWarm ? '#2D5A3D' : isPink ? '#6B55A3' : '#4361EE' }}
              data-testid="start-trial-btn"
            >
              Start Free Trial
            </Button>
          ) : (
            <Button
              onClick={() => showFeaturePaywall('wishlist')}
              className="w-full py-3.5 rounded-2xl text-[15px] font-bold border-0 shadow-none"
              style={{ background: isWarm ? '#F0E6D8' : isPink ? '#EDE6F5' : '#EEF1FE', color: isWarm ? '#2D5A3D' : isPink ? '#6B55A3' : '#4361EE' }}
              data-testid="upgrade-premium-btn"
            >
              Start Free Trial
            </Button>
          )}
        </div>
      </div>
    );
  }

  const fetchProductInfo = async () => {
    if (!newUrl.trim()) return;
    
    setIsLoading(true);
    try {
      const response = await fetch(`${API_URL}/api/scrape-product`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ url: newUrl.trim() })
      });
      const data = await response.json();
      
      if (data.success) {
        if (data.title && !newItem) setNewItem(data.title);
        if (data.price && !newValue) setNewValue(data.price.toString());
        if (data.image_url) setNewImageUrl(data.image_url);
        customToast.success("Product fetched!", "Info auto-filled");
      } else {
        customToast.error("Failed to fetch", data.error || "Could not fetch product info");
      }
    } catch (error) {
      customToast.error("Failed to fetch", "Check your connection");
    } finally {
      setIsLoading(false);
    }
  };

  const handlePhotoUpload = async (e) => {
    const file = e.target.files?.[0];
    if (!file) return;
    if (file.size > 5 * 1024 * 1024) {
      customToast.error("File too large", "Max 5MB");
      return;
    }
    setIsUploading(true);
    try {
      const reader = new FileReader();
      reader.onload = () => {
        // Resize image to keep data small
        const img = new Image();
        img.onload = () => {
          const canvas = document.createElement('canvas');
          const maxSize = 300;
          let w = img.width, h = img.height;
          if (w > h) { h = (h / w) * maxSize; w = maxSize; }
          else { w = (w / h) * maxSize; h = maxSize; }
          canvas.width = w;
          canvas.height = h;
          canvas.getContext('2d').drawImage(img, 0, 0, w, h);
          const dataUrl = canvas.toDataURL('image/jpeg', 0.7);
          setNewImageUrl(dataUrl);
          setNewEmoji('');
          setIsUploading(false);
          customToast.success("Photo added!");
        };
        img.src = reader.result;
      };
      reader.readAsDataURL(file);
    } catch {
      customToast.error("Upload failed", "Try again");
      setIsUploading(false);
    }
  };

  const addItem = () => {
    if (newItem.trim() && newValue && parseFloat(newValue) > 0) {
      const item = { 
        id: Date.now(), 
        name: newItem.trim(), 
        value: parseFloat(newValue), 
        purchased: false, 
        date_added: new Date().toISOString(), 
        date_purchased: null,
        product_url: newUrl.trim() || null,
        image_url: newImageUrl.trim() || null,
        emoji: newImageUrl.trim() ? null : (newEmoji || null)
      };
      
      saveUserData({ wishlist_items: [item, ...wishlistItems] });
      setNewItem(''); setNewValue(''); setNewUrl(''); setNewImageUrl(''); setNewEmoji('');
      customToast.success("Added to wishlist!", newItem.trim());
    }
  };

  const toggleSelected = (id) => {
    setSelectedItems(prev => 
      prev.includes(id) ? prev.filter(i => i !== id) : [...prev, id]
    );
  };

  const removeItem = (id) => {
    setSelectedItems(prev => prev.filter(i => i !== id));
    saveUserData({ wishlist_items: wishlistItems.filter(i => i.id !== id) });
  };

  const saveTaxRate = () => {
    const rate = parseFloat(taxRateInput);
    if (!isNaN(rate) && rate >= 0 && rate <= 100) {
      saveUserData({ wishlist_tax_rate: rate });
      setEditingTaxRate(false);
      setTaxRateInput('');
      customToast.success("Tax rate updated!", `Now ${rate}%`);
    } else {
      customToast.error("Invalid tax rate", "Please enter 0-100");
    }
  };

  const unpurchased = wishlistItems.filter(i => !i.purchased);
  const totalWishlist = unpurchased.reduce((sum, i) => sum + i.value, 0);
  const totalWishlistWithTax = totalWishlist * (1 + taxRate / 100);
  const selectedTotal = wishlistItems
    .filter(i => selectedItems.includes(i.id) && !i.purchased)
    .reduce((sum, i) => sum + i.value, 0);
  const selectedTotalWithTax = selectedTotal * (1 + taxRate / 100);

  return (
    <div className="max-w-3xl mx-auto px-4 pt-4 pb-8 wy-plan-wish" style={{ fontFamily: "'DM Sans', sans-serif", transform: kbShift ? `translateY(-${kbShift}px)` : undefined, transition: 'transform 0.25s ease' }}>
      {showWishlistInfo && createPortal(
        <div className="fixed inset-0 z-50" style={{ touchAction: 'none' }}>
          <div className="absolute inset-0 bg-black/40" onClick={() => setShowWishlistInfo(false)} />
          <div className="absolute bottom-0 left-0 right-0 bg-white rounded-t-3xl p-6 pb-10 shadow-xl overflow-y-auto" style={{ maxHeight: '88vh', touchAction: 'pan-y' }}>
            <div className="w-10 h-1 bg-gray-200 rounded-full mx-auto mb-5" />
            <h3 className="font-normal text-gray-800 mb-4" style={{ fontFamily: "'DM Serif Display', serif", fontSize: '20px' }}>About the Wishlist</h3>
            <div className="space-y-4">
              <div className="flex gap-3"><span className="text-xl flex-shrink-0">🛍️</span><div><p className="text-[15px] font-semibold text-gray-700 mb-0.5">Save items you want</p><p className="text-[13px] text-gray-500 leading-relaxed">Add things you're considering buying before you commit. The total at the top includes tax so you can see the full cost of everything on your list.</p></div></div>
              <div className="flex gap-3"><span className="text-xl flex-shrink-0">🧾</span><div><p className="text-[15px] font-semibold text-gray-700 mb-0.5">Tax rate</p><p className="text-[13px] text-gray-500 leading-relaxed">Edit your tax rate so all totals are accurate for your region.</p></div></div>
              <div className="flex gap-3"><span className="text-xl flex-shrink-0">🤔</span><div><p className="text-[15px] font-semibold text-gray-700 mb-0.5">Should I Buy This?</p><p className="text-[13px] text-gray-500 leading-relaxed">Seven quick questions about need, research, timing and budget. You get a purchase score out of 100 and a verdict: Go for it, Sleep on it or Skip it.</p></div></div>
              <div className="flex gap-3"><span className="text-xl flex-shrink-0">📈</span><div><p className="text-[15px] font-semibold text-gray-700 mb-0.5">What Am I Giving Up?</p><p className="text-[13px] text-gray-500 leading-relaxed">Shows what the money could grow to over 5 to 20 years if you invested it in an index fund instead. Rates are historical averages, not a guarantee.</p></div></div>
              <div className="flex gap-3"><span className="text-xl flex-shrink-0">💭</span><div><p className="text-[15px] font-semibold text-gray-700 mb-0.5">Think it over</p><p className="text-[13px] text-gray-500 leading-relaxed">Tap Think it over on any item to run both with its price already filled in. Your verdict is saved on the item, and you can retake it any time.</p></div></div>
            </div>
            <button onClick={() => setShowWishlistInfo(false)} className="mt-6 w-full py-3 rounded-2xl theme-bg-500 text-white font-semibold text-[15px]">Got it</button>
          </div>
        </div>,
        document.body
      )}
      {thinkItem && (
        <ThinkItOverSheet
          item={thinkItem}
          taxRate={taxRate}
          theme={theme}
          onClose={() => setThinkItem(null)}
          onSaveResult={(r) => saveUserData({
            wishlist_items: wishlistItems.map((i) => (i.id === thinkItem.id ? { ...i, quiz_result: r } : i)),
          })}
          onRemove={() => { removeItem(thinkItem.id); setThinkItem(null); }}
        />
      )}
      {emojiPickerOpen && createPortal(
        <div className="fixed inset-0 z-50" style={{ touchAction: 'none' }}>
          <div className="absolute inset-0 bg-black/40" onClick={() => { setEmojiTyping(false); setEmojiPickerOpen(false); }} />
          <div
            className="absolute bottom-0 left-0 right-0 bg-white rounded-t-3xl p-6 shadow-xl"
            style={{ paddingBottom: '400px' }}
          >
            <div className="w-10 h-1 bg-gray-200 rounded-full mx-auto mb-5" />
            <h3 className="font-normal text-gray-800 mb-4" style={{ fontFamily: "'DM Serif Display', serif", fontSize: '20px' }}>Pick an emoji</h3>
            <div className="grid grid-cols-7 gap-1.5">
              {WISHLIST_EMOJIS.map((e) => (
                <button
                  key={e}
                  onClick={() => { setNewEmoji(e); setNewImageUrl(''); setEmojiPickerOpen(false); }}
                  className="aspect-square rounded-xl flex items-center justify-center text-[22px]"
                  style={{ background: newEmoji === e ? (isPink ? '#FBE3EE' : isWarm ? '#F0E6D8' : '#EEF1FE') : '#F7F4F6' }}
                >
                  {e}
                </button>
              ))}
            </div>
            <div className="flex gap-2 mt-4">
              <Input
                type="text"
                placeholder="Or type any emoji"
                value={customEmoji}
                onChange={(e) => setCustomEmoji(e.target.value.slice(0, 8))}
                onFocus={() => setEmojiTyping(true)}
                onBlur={() => setEmojiTyping(false)}
                className="h-10 border-gray-200 text-[15px] flex-1"
                data-testid="wishlist-custom-emoji"
              />
              <Button
                onClick={() => {
                  const v = customEmoji.trim();
                  if (!v) return;
                  setNewEmoji(v); setNewImageUrl(''); setCustomEmoji(''); setEmojiTyping(false); setEmojiPickerOpen(false);
                }}
                disabled={!customEmoji.trim()}
                className={`h-10 px-4 text-white ${isPink ? 'bg-pink-500 hover:bg-pink-600' : isWarm ? 'bg-[#2D5A3D] hover:bg-[#1B4027]' : 'bg-indigo-500 hover:bg-indigo-600'}`}
              >
                Use
              </Button>
            </div>
          </div>
        </div>,
        document.body
      )}
      {/* plan_10: Money plan - title, then both totals in one card */}
      {plan && (
        <div data-testid="plan-wishlist-top">
          <div className="flex items-center justify-between" style={{ gap: 12 }}>
            <div style={{ minWidth: 0 }}>
              <h1 className="flex items-center" style={{ margin: 0, gap: 8 }}>your wishlist<button type="button" aria-label="How it works" onClick={() => setShowWishlistInfo(true)} style={{ background: '#FFFFFF', border: 0, cursor: 'pointer', borderRadius: '50%', width: 22, height: 22, display: 'inline-flex', alignItems: 'center', justifyContent: 'center', fontSize: 11.5, fontWeight: 600, color: '#6B6B73', padding: 0, flexShrink: 0 }}>i</button></h1>{/* plan_12: no subtitle; plan_41: the i */}
            </div>
          </div>
          <div style={{ background: '#FFFFFF', borderRadius: 28, padding: 12, marginTop: 16, marginBottom: 16 }} data-testid="wishlist-hero">
            <div className="grid grid-cols-2" style={{ gap: 10 }}>
              {[
                { bg: '#ECE6FA', ink: '#7A5DD3', emoji: '\u{1F6CD}️', label: 'Wishlist total', value: totalWishlistWithTax, id: 'wishlist-total-box' },
                { bg: '#DFF2E7', ink: '#2F8756', emoji: '✅', label: 'Selected', value: selectedTotalWithTax, id: 'selected-total-box' },
              ].map((t) => (
                <div key={t.id} style={{ background: t.bg, borderRadius: 24, padding: 14, minWidth: 0 }} data-testid={t.id}>
                  <span className="flex items-center justify-center" style={{ width: 32, height: 32, borderRadius: 999, background: 'rgba(255,255,255,0.75)', fontSize: 16, marginBottom: 8 }}>{t.emoji}</span>
                  <span style={{ display: 'block', fontSize: 12.5, color: '#55525F' }}>{t.label}</span>
                  <span style={{ display: 'block', fontSize: 19, fontWeight: 700, letterSpacing: '-0.5px', color: t.ink, marginTop: 2 }}>{formatCurrencyWhole(t.value)}</span>
                  <button
                    type="button"
                    onClick={() => { setEditingTaxRate(true); setTaxRateInput(taxRate.toString()); }}
                    className="flex items-center"
                    style={{ gap: 4, marginTop: 2, fontSize: 12, fontWeight: 500, color: t.ink, background: 'none', border: 0, padding: 0 }}
                    data-testid="edit-tax-btn"
                  >
                    with {taxRate}% tax <Edit3 style={{ width: 11, height: 11, opacity: 0.75 }} />
                  </button>
                </div>
              ))}
            </div>
          </div>
        </div>
      )}
      {!plan && (<>
      {/* Purple: the header and the two totals sit in the gradient band (wy-ph). */}
      <div className="wy-ph" style={{ '--ph-lift': '1rem' }}>
      {/* Floating header */}
      <div className="mb-3">
        <div style={{ display:'flex', alignItems:'center', gap:'8px', marginBottom: '2px' }}>
          <h1 style={{ fontFamily: "'DM Serif Display', serif", fontSize: '28px', fontWeight: 400, color: '#1A1A1F', letterSpacing: '-0.6px' }}>Wishlist</h1>
          <button onClick={() => setShowWishlistInfo(true)} style={{ background:'#F4F1F8', border:'1px solid #EFEAF1', cursor:'pointer', borderRadius:'50%', width:'20px', height:'20px', display:'inline-flex', alignItems:'center', justifyContent:'center', fontSize:'11px', fontWeight:700, color:'#6B6B73', padding:0, flexShrink:0 }}>i</button>
        </div>
        <p style={{ fontSize: '12px', color: '#6B6B73' }}>Avoid impulse purchases, add them here instead!</p>
      </div>

      {/* Floating totals - no outer wrapper */}
      <div className="grid grid-cols-2 gap-2 mb-4" data-testid="wishlist-hero">
        {/* Wishlist Total */}
        <div className="rounded-[18px] wy-tile wy-tile-ink" style={{ background: tileColors.lavender.bg, padding: '12px 11px 10px' }} data-testid="wishlist-total-box">
          <div className="rounded-lg flex items-center justify-center wy-chip" data-wy-tint="1" style={{ width: '30px', height: '30px', background: 'rgba(255,255,255,0.55)', fontSize: '16px', marginBottom: '8px' }}>🛍️</div>
          <span className="wy-tile-label" style={{ display: 'block', fontSize: '11px', fontWeight: 700, letterSpacing: '0.05em', textTransform: 'uppercase', color: tileColors.lavender.text, marginBottom: '3px' }}>
            Wishlist Total
          </span>
          <p className="wy-tile-value" style={{ fontSize: '20px', fontWeight: 700, letterSpacing: '-0.3px', color: tileColors.lavender.text }}>
            {formatCurrencyWhole(totalWishlistWithTax)}
          </p>
          <div style={{ display: 'flex', alignItems: 'center', gap: '4px', marginTop: '2px' }}>
            <span style={{ fontSize: '10px', fontWeight: 600, color: tileColors.lavender.text, opacity: 0.7 }}>with {taxRate}% tax</span>
            <button onClick={() => { setEditingTaxRate(true); setTaxRateInput(taxRate.toString()); }} style={{ background: 'none', border: 'none', padding: '2px', cursor: 'pointer', display: 'inline-flex', alignItems: 'center', justifyContent: 'center' }} data-testid="edit-tax-btn">
              <Edit3 style={{ width: '9px', height: '9px', color: tileColors.lavender.text, opacity: 0.6 }} />
            </button>
          </div>
        </div>

        {/* Selected Total */}
        <div className="rounded-[18px] wy-tile wy-tile-ink" style={{ background: tileColors.green.bg, padding: '12px 11px 10px' }} data-testid="selected-total-box">
          <div className="rounded-lg flex items-center justify-center wy-chip" data-wy-tint="1" style={{ width: '30px', height: '30px', background: 'rgba(255,255,255,0.55)', fontSize: '16px', marginBottom: '8px' }}>✅</div>
          <span className="wy-tile-label" style={{ display: 'block', fontSize: '11px', fontWeight: 700, letterSpacing: '0.05em', textTransform: 'uppercase', color: tileColors.green.text, marginBottom: '3px' }}>
            Selected Total
          </span>
          <p className="wy-tile-value" style={{ fontSize: '20px', fontWeight: 700, letterSpacing: '-0.3px', color: tileColors.green.text }}>
            {formatCurrencyWhole(selectedTotalWithTax)}
          </p>
          <div style={{ display: 'flex', alignItems: 'center', gap: '4px', marginTop: '2px' }}>
            <span style={{ fontSize: '10px', fontWeight: 600, color: tileColors.green.text, opacity: 0.7 }}>with {taxRate}% tax</span>
            <button onClick={() => { setEditingTaxRate(true); setTaxRateInput(taxRate.toString()); }} style={{ background: 'none', border: 'none', padding: '2px', cursor: 'pointer', display: 'inline-flex', alignItems: 'center', justifyContent: 'center' }} data-testid="edit-tax-btn">
              <Edit3 style={{ width: '9px', height: '9px', color: tileColors.green.text, opacity: 0.6 }} />
            </button>
          </div>
        </div>

      </div>
      </div>
      </>)}

      {/* Tax Rate Editor (inline form, only shown when editing) */}
      {editingTaxRate && (
        <div className="flex justify-center mb-4">
          <div className="flex items-center gap-2 bg-white rounded-xl p-2 shadow-sm">
            <Percent className="w-4 h-4 text-gray-400" />
            <Input
              type="number"
              value={taxRateInput}
              onChange={(e) => setTaxRateInput(e.target.value)}
              placeholder={`${taxRate}`}
              className="w-20 h-8 text-[15px]"
              min="0"
              max="100"
              step="0.5"
              data-testid="tax-rate-input"
            />
            <Button 
              onClick={saveTaxRate} 
              size="sm" 
              className="h-8 px-3 theme-bg-500 hover:theme-bg-600 text-white"
              data-testid="save-tax-btn"
            >
              Save
            </Button>
            <Button 
              onClick={() => { setEditingTaxRate(false); setTaxRateInput(''); }} 
              size="sm" 
              variant="ghost"
              className="h-8 px-2 text-gray-500"
            >
              <X className="w-4 h-4" />
            </Button>
          </div>
        </div>
      )}

      {/* Before You Buy - the buying tools, at the top so new users meet them */}
      <p className="text-[13px] font-bold uppercase tracking-[0.08em] px-1 mb-2" style={{ color: isPink ? '#7B5EA7' : isWarm ? '#2D5A3D' : '#4361EE' }}><span className="wy-star">✦ </span>Before you buy</p>
      <div
        className={plan ? 'wy-plan-tools' : 'grid grid-cols-2 gap-2.5 mb-4'}
        style={plan ? { background: '#FFFFFF', borderRadius: 28, padding: '4px 0', marginBottom: 16, overflow: 'hidden' } : undefined}
        data-testid="wishlist-buying-tools"
      >
        <QuizBox variant={plan ? 'row' : 'tile'} />
        <InvestmentCostCalculator variant={plan ? 'row' : 'tile'} />
      </div>

      {/* Add to Wishlist Section */}
      <p ref={addEyebrowRef} className="text-[13px] font-bold uppercase tracking-[0.08em] px-1 mb-2" style={{ color: isPink ? '#7B5EA7' : isWarm ? '#2D5A3D' : '#4361EE', scrollMarginTop: 'calc(env(safe-area-inset-top, 0px) + 16px)' }}><span className="wy-star">✦ </span>Add to wishlist</p>
      <div ref={addCardRef} onFocus={onAddFocus} onBlur={onAddBlur} className="bg-white rounded-2xl p-4 mb-4 shadow-sm" data-tour-target="wishlistAddCard">
        <div className="space-y-2">
          {/* URL Input */}
          <div className="flex gap-2">
            <div className="relative flex-1">
              <LinkIcon className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-gray-400" />
              <Input 
                type="url" 
                placeholder="Paste product link (optional)" 
                value={newUrl} 
                onChange={(e) => setNewUrl(e.target.value)} 
                className="pl-10 h-10 border-gray-200 text-[15px]" 
                data-testid="wishlist-url-input"
              />
            </div>
            <Button 
              onClick={fetchProductInfo} 
              disabled={!newUrl.trim() || isLoading}
              className={`h-10 px-4 text-white ${isPink ? 'bg-pink-500 hover:bg-pink-600' : isWarm ? 'bg-[#2D5A3D] hover:bg-[#1B4027]' : 'bg-indigo-500 hover:bg-indigo-600'}`}
              data-testid="fetch-product-btn"
            >
              {isLoading ? <Loader2 className="w-4 h-4 animate-spin" /> : 'Fetch'}
            </Button>
          </div>
          
          {/* Name Input */}
          <Input 
            type="text" 
            placeholder="What do you want?" 
            value={newItem} 
            onChange={(e) => setNewItem(e.target.value)} 
            className="h-10 border-gray-200 text-[15px]" 
            data-testid="wishlist-name-input"
          />
          
          {/* Price and Add Row */}
          <div className="flex gap-2">
            <div className="relative flex-1">
              <span className="absolute left-3 top-1/2 -translate-y-1/2 text-[15px] font-semibold text-gray-400">{getCurrentCurrency().symbol}</span>
              <Input 
                type="number" 
                placeholder="0.00" 
                value={newValue} 
                onChange={(e) => setNewValue(e.target.value)} 
                className="pl-10 h-10 border-gray-200 text-[15px]" 
                min="0" 
                step="0.01" 
                data-testid="wishlist-price-input"
              />
            </div>
            <button
              type="button"
              onClick={() => setEmojiPickerOpen(true)}
              className="h-10 w-10 p-0 rounded-full flex items-center justify-center flex-shrink-0 text-[18px]"
              style={{ background: isPink ? '#FBE3EE' : isWarm ? '#F0E6D8' : '#EEF1FE' }}
              aria-label="Use an emoji instead of a photo"
              data-testid="wishlist-emoji-btn"
            >
              {newEmoji || '\uD83D\uDCF2'}
            </button>
            <label
              className={`h-10 w-10 p-0 rounded-full flex items-center justify-center cursor-pointer text-white flex-shrink-0 ${
                isUploading ? 'opacity-50' : ''
              } ${isPink ? 'bg-pink-400 hover:bg-pink-500' : isWarm ? 'bg-[#8B7355] hover:bg-[#7C5C3A]' : 'bg-indigo-400 hover:bg-indigo-500'}`}
              data-testid="wishlist-photo-upload-btn"
            >
              {isUploading ? <Loader2 className="w-4 h-4 animate-spin" /> : <Camera className="w-4 h-4" />}
              <input
                type="file"
                accept="image/*"
                className="hidden"
                onChange={handlePhotoUpload}
                disabled={isUploading}
              />
            </label>
            <Button
              onClick={addItem}
              className={`h-10 w-10 p-0 rounded-full text-white ${isPink ? 'bg-pink-500 hover:bg-pink-600' : isWarm ? 'bg-[#2D5A3D] hover:bg-[#1B4027]' : 'bg-indigo-500 hover:bg-indigo-600'}`}
              data-testid="add-wishlist-item-btn"
            >
              <Plus className="w-5 h-5" />
            </Button>
          </div>
          {!newImageUrl && newEmoji && (
            <div className="relative w-16 h-16 rounded-lg overflow-hidden mt-2 flex items-center justify-center text-[34px]" style={{ background: tileColors.lavender.bg }}>
              {newEmoji}
              <button
                onClick={() => setNewEmoji('')}
                className="absolute top-0 right-0 bg-black/50 rounded-bl-lg p-0.5"
              >
                <X className="w-3 h-3 text-white" />
              </button>
            </div>
          )}
          {newImageUrl && (
            <div className="relative w-16 h-16 rounded-lg overflow-hidden border border-gray-200 mt-2">
              <img src={newImageUrl} alt="Preview" className="w-full h-full object-cover" />
              <button
                onClick={() => setNewImageUrl('')}
                className="absolute top-0 right-0 bg-black/50 rounded-bl-lg p-0.5"
              >
                <X className="w-3 h-3 text-white" />
              </button>
            </div>
          )}
        </div>
      </div>
      {/* Mark as Purchased Button */}
      {selectedItems.length > 0 && (
        <div className="flex gap-2 mb-4">
          <Button 
            onClick={() => {
              const updated = wishlistItems.map(item => {
                if (selectedItems.includes(item.id) && !item.purchased) {
                  return { ...item, purchased: true, date_purchased: new Date().toISOString() };
                }
                return item;
              });
              saveUserData({ wishlist_items: updated });
              setSelectedItems([]);
              customToast.success("Purchased!", "Items marked as bought");
            }}
            className={`flex-1 text-white h-10 rounded-xl ${isPink ? 'bg-pink-500 hover:bg-pink-600' : isWarm ? 'bg-[#2D5A3D] hover:bg-[#1B4027]' : 'bg-indigo-500 hover:bg-indigo-600'}`}
            data-testid="mark-purchased-btn"
          >
            <Check className="w-4 h-4 mr-2" /> Mark Selected as Purchased
          </Button>
          <Button 
            onClick={() => setSelectedItems([])}
            variant="outline"
            className="border-gray-200 h-10 rounded-xl"
          >
            Clear
          </Button>
        </div>
      )}

      {/* Wishlist Grid */}
      <div className="flex items-center justify-between px-1 mb-2">
        <p className="text-[13px] font-bold uppercase tracking-[0.08em]" style={{ color: isPink ? '#7B5EA7' : isWarm ? '#2D5A3D' : '#4361EE' }}><span className="wy-star">✦ </span>Your wishlist</p>
        <span className="text-[13px] text-gray-500">{wishlistItems.length} items</span>
      </div>
      <div className="bg-white rounded-2xl shadow-sm overflow-hidden">
        
        {wishlistItems.length === 0 ? (
          <div className="p-8 text-center text-gray-400">
            <ShoppingBag className="w-10 h-10 mx-auto mb-2 opacity-30" />
            <p className="text-[15px]">Your wishlist is empty</p>
          </div>
        ) : (
          <div className="p-3">
            <div className="grid grid-cols-2 gap-3">
              {[...wishlistItems].sort((a, b) => (a.purchased ? 1 : 0) - (b.purchased ? 1 : 0)).map(item => (
                <div 
                  key={item.id} 
                  className={`relative rounded-2xl overflow-hidden border ${
                    item.purchased 
                      ? (isPink ? 'border-pink-200 bg-pink-50/30' : isWarm ? 'border-[#D8E8DC] bg-[#F0F5F1]/30' : 'border-indigo-200 bg-indigo-50/30') 
                      : selectedItems.includes(item.id)
                        ? `border-2 ${isPink ? 'border-pink-400' : isWarm ? 'border-[#2D5A3D]' : 'border-indigo-400'}`
                        : 'border-gray-100 bg-white hover:shadow-md'
                  } transition-all`}
                  data-testid={`wishlist-item-${item.id}`}
                >
                  {/* Image */}
                  <div className="aspect-square bg-gray-50 relative">
                    {item.image_url ? (
                      <img 
                        src={item.image_url} 
                        alt={item.name} 
                        className={`w-full h-full object-cover ${item.purchased ? 'opacity-50' : ''}`}
                        onError={(e) => {
                          e.target.style.display = 'none';
                        }}
                      />
                    ) : item.emoji ? (
                      <div
                        className={`absolute inset-0 flex items-center justify-center ${item.purchased ? 'opacity-50' : ''}`}
                        style={{ background: plan ? ['#FAE4ED', '#DFE9F9', '#DFF2E7', '#ECE6FA', '#FCF3D2'][Math.abs(Number(item.id) || 0) % 5] : tileColors.lavender.bg, fontSize: plan ? '52px' : '64px' }}
                      >
                        {item.emoji}
                      </div>
                    ) : (
                      <div className="absolute inset-0 flex items-center justify-center">
                        <ShoppingBag className={`w-10 h-10 ${item.purchased ? (isPink ? 'text-pink-300' : isWarm ? 'text-[#7C9070]' : 'text-indigo-300') : 'text-gray-200'}`} />
                      </div>
                    )}
                    
                    {/* Selection Circle */}
                    {!item.purchased && (
                      <div className="absolute top-2 left-2">
                        <button
                          onClick={(e) => { e.stopPropagation(); toggleSelected(item.id); }}
                          className={`w-7 h-7 rounded-full border-2 flex items-center justify-center cursor-pointer transition-all shadow-sm ${
                            selectedItems.includes(item.id)
                              ? `${isPink ? 'bg-pink-500 border-pink-500' : isWarm ? 'bg-[#2D5A3D] border-[#2D5A3D]' : 'bg-indigo-500 border-indigo-500'}`
                              : 'bg-white border-gray-300 hover:border-gray-400'
                          }`}
                          data-testid={`select-item-${item.id}`}
                        >
                          {selectedItems.includes(item.id) && (
                            <Check className="w-4 h-4 text-white" />
                          )}
                        </button>
                      </div>
                    )}
                    
                    {/* Purchased badge */}
                    {item.purchased && (
                      <div className={`absolute top-2 left-2 text-white text-[10px] px-2 py-0.5 rounded-full font-semibold ${isPink ? 'bg-pink-500' : isWarm ? 'bg-[#2D5A3D]' : 'bg-indigo-500'}`}>
                        Purchased
                      </div>
                    )}
                    
                    {/* Delete button */}
                    <button 
                      onClick={() => removeItem(item.id)} 
                      className="absolute top-2 right-2 w-7 h-7 rounded-full bg-white/90 hover:bg-white flex items-center justify-center text-gray-400 hover:text-red-500 shadow-sm transition-colors"
                      data-testid={`delete-item-${item.id}`}
                    >
                      <X className="w-4 h-4" />
                    </button>
                    
                    {/* Link button */}
                    {item.product_url && (
                      <a 
                        href={item.product_url} 
                        target="_blank" 
                        rel="noopener noreferrer"
                        className="absolute bottom-2 right-2 w-7 h-7 rounded-full bg-white/90 hover:bg-white flex items-center justify-center text-gray-400 hover:text-indigo-500 shadow-sm transition-colors"
                      >
                        <ExternalLink className="w-3.5 h-3.5" />
                      </a>
                    )}
                  </div>
                  
                  {/* Info */}
                  <div className="p-2.5">
                    <p className={`font-medium text-[14px] truncate ${item.purchased ? 'text-gray-400 line-through' : 'text-gray-700'}`}>
                      {item.name}
                    </p>
                    <p className={`font-bold text-[15px] ${isPink ? 'text-pink-500' : isWarm ? 'text-[#2D5A3D]' : 'text-indigo-500'}`}>
                      {formatCurrency(item.value)}
                    </p>
                    {!item.purchased && (() => {
                      const v = item.quiz_result && VERDICT_STYLE[item.quiz_result.verdict];
                      return v ? (
                        <button
                          onClick={() => setThinkItem(item)}
                          className="mt-2 w-full rounded-xl px-2.5 py-1.5 flex items-center justify-between text-[11.5px] font-semibold"
                          style={{ background: v.bg, color: v.color }}
                          data-testid={`think-result-${item.id}`}
                        >
                          <span className="truncate">{v.emoji} {v.label}</span>
                          <span className="font-medium flex-shrink-0" style={{ opacity: 0.75 }}>Retake</span>
                        </button>
                      ) : (
                        <button
                          onClick={() => setThinkItem(item)}
                          className="mt-2 w-full rounded-xl py-1.5 text-[12px] font-semibold"
                          style={{
                            background: isPink ? '#F3ECF7' : isWarm ? '#F0E6D8' : '#EEF1FE',
                            color: isPink ? '#7B5EA7' : isWarm ? '#2D5A3D' : '#4361EE',
                          }}
                          data-testid={`think-it-over-${item.id}`}
                        >
                          {'\uD83E\uDD14'} Think it over
                        </button>
                      );
                    })()}
                  </div>
                </div>
              ))}
            </div>
          </div>
        )}
      </div>
    </div>
  );
};

export default WishlistTab;
